import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container, Typography, TextField, Button, Paper, Stack, Box,
  Snackbar, Alert, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Zoom, Avatar, useTheme, useMediaQuery, 
  Tooltip, TablePagination
} from "@mui/material";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import LogoutIcon from "@mui/icons-material/Logout";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  validateComplexPassword,
  PASSWORD_REQUIREMENTS,
} from "../../utils/passwordValidation";

const BASE_URL = `${process.env.REACT_APP_API_URL}/api/admin/company`;

const BenAdminDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState({ name: "", username: "", email: "", password: "" });

  const [openForm, setOpenForm] = useState(false);
  const [openPass, setOpenPass] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const token = localStorage.getItem("token");

  // --- Theme Styles (White Background, Black Text, Yellow Buttons) ---
  const yellowButton = {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  };

  const whiteCard = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  };

  const whiteInput = {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
  };

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // --- Logic ---
  const fetchCompanies = useCallback(async (manual = false) => {
    try {
      const res = await axios.get(`${BASE_URL}/all`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setCompanies(res.data.companies || res.data);
      
      if (manual) {
        showSnackbar("Table Updated", "success");
      }
    } catch (err) { 
      showSnackbar("Registry Link Failed", "error"); 
    }
  }, [token]);

  // Initial silent load
  useEffect(() => { 
    fetchCompanies(false); 
  }, [fetchCompanies]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  // Export Placeholders
  const exportToExcel = () => {
    if (filteredCompanies.length === 0) {
      showSnackbar("No records to export", "warning");
      return;
    }
    showSnackbar("Generating Excel Stream...");
    // Add actual export logic here
  };
  
  const exportToPDF = () => {
    if (filteredCompanies.length === 0) {
      showSnackbar("No records to export", "warning");
      return;
    }
    showSnackbar("Rendering PDF Buffer...");
    // Add actual export logic here
  };

  const handleCreateOrUpdate = async () => {
    if (!newCompany.name || !newCompany.username || (editIndex === null && !newCompany.password)) return;
    try {
      // Ensure username is lowercase
      const companyData = {
        ...newCompany,
        username: newCompany.username.toLowerCase()
      };

      if (editIndex !== null) {
        const companyId = companies[editIndex]._id;
        await axios.put(`${BASE_URL}/${companyId}`, companyData, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Entity Updated");
      } else {
        await axios.post(`${BASE_URL}/create`, companyData, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Entity Created");
      }
      setOpenForm(false);
      fetchCompanies();
      setNewCompany({ name: "", username: "", email: "", password: "" });
    } catch (err) { 
      showSnackbar("Operation Failed", "error"); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Purge this entity?")) return;
    try {
      await axios.delete(`${BASE_URL}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(companies.filter((c) => c._id !== id));
      showSnackbar("Entity Purged", "warning");
    } catch (err) { 
      showSnackbar("Purge Failed", "error"); 
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await axios.patch(`${BASE_URL}/${id}/status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(companies.map((c) => (c._id === id ? res.data.company : c)));
      showSnackbar(`Status: ${res.data.company.status}`);
    } catch (err) { 
      showSnackbar("Status Shift Failed", "error"); 
    }
  };

  const handleTogglePayment = async (id) => {
    try {
      const res = await axios.patch(`${BASE_URL}/${id}/payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(companies.map((c) => (c._id === id ? res.data.company : c)));
      showSnackbar(`Payment: ${res.data.company.paymentStatus}`);
    } catch (err) { 
      showSnackbar("Payment Shift Failed", "error"); 
    }
  };

  const handleLogout = () => { 
    localStorage.removeItem("token"); 
    navigate("/login"); 
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return showSnackbar("Keys do not match", "error");
    }
    const validation = validateComplexPassword(passwordForm.newPassword);
    if (!validation.valid) {
      return showSnackbar(validation.msg, "error");
    }
    try {
      await axios.patch(`${BASE_URL}/change-password`,
        { oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("Security Key Updated");
      setOpenPass(false);
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) { 
      showSnackbar("Security Update Failed", "error"); 
    }
  };

  // Filter and pagination logic
  const filteredCompanies = useMemo(() => {
    return companies.filter(
      (c) => c.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        c.username?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [companies, debouncedSearch]);

  const paginatedCompanies = useMemo(() => {
    return filteredCompanies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredCompanies, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box minHeight="100vh" sx={{ backgroundColor: "#f5f5f5", py: isMobile ? 3 : 5 }}>
      <Container maxWidth="lg">
        
        {/* --- 1. SUPER TERMINAL HEADER --- */}
        <Paper sx={{ p: 3, mb: 4, ...whiteCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "#FFD600", color: "#000000" }}>
              <BusinessIcon />
            </Avatar>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={900} sx={{ color: "#000000" }}>
              ADMIN TERMINAL
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export Excel">
              <IconButton onClick={exportToExcel} sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}>
                <GridOnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export PDF">
              <IconButton onClick={exportToPDF} sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ 
                borderColor: "#ff4444", 
                color: "#ff0000", 
                "&:hover": { 
                  borderColor: "#ff0000", 
                  bgcolor: "rgba(255, 68, 68, 0.1)" 
                } 
              }}
            >
              Logout
            </Button>
          </Stack>
        </Paper>

        {/* --- 2. ACTION BAR (TOOLBAR) --- */}
        <Paper
          sx={{
            px: 3,
            mb: 4,
            ...whiteCard,
            height: isMobile ? 'auto' : '70px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ width: '100%', py: isMobile ? 2 : 0 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { 
                  setEditIndex(null); 
                  setNewCompany({ name: "", username: "", email: "", password: "" }); 
                  setOpenForm(true); 
                }}
                sx={yellowButton}
              >
                New
              </Button>
              <IconButton 
                onClick={() => setOpenPass(true)} 
                sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}
              >
                <SecurityIcon />
              </IconButton>
            </Stack>

            <Box display="flex" alignItems="center" gap={1} flexGrow={isMobile ? 1 : 0}>
              <TextField
                placeholder="Search registries..."
                size="small"
                fullWidth={isMobile}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={whiteInput}
              />
              <IconButton
                onClick={() => fetchCompanies(true)}
                sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600" }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Stack>
        </Paper>

        {/* --- 3. DATA VIEW with Scroll and Pagination --- */}
        <Paper sx={whiteCard}>
          <Box 
            sx={{ 
              maxHeight: "calc(100vh - 350px)",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#FFD600",
                borderRadius: "4px",
              },
            }}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              {paginatedCompanies.map((c, index) => (
                <Paper key={c._id} sx={{ p: 2, ...whiteCard, "&:hover": { bgcolor: "#fafafa" } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 2 : 0}>
                    <Box display="flex" alignItems="center" gap={2} width={isMobile ? "100%" : "auto"}>
                      <Avatar sx={{ bgcolor: "#FFF5E0", border: "1px solid #FFD600" }}>
                        <BusinessIcon sx={{ color: "#FFD600" }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#000000" }}>{c.name}</Typography>
                        <Typography variant="caption" sx={{ color: "#666666" }}>{c.username} | {c.email}</Typography>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={isMobile ? "space-between" : "flex-end"} width={isMobile ? "100%" : "auto"}>
                      <Chip
                        label={c.status}
                        onClick={() => handleToggleStatus(c._id)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: c.status === "ACTIVE" ? "#4caf50" : "#f44336",
                          color: "#fff",
                          fontWeight: "bold"
                        }}
                      />
                      <Chip
                        label={c.paymentStatus}
                        variant="outlined"
                        onClick={() => handleTogglePayment(c._id)}
                        sx={{
                          cursor: "pointer",
                          color: c.paymentStatus === "PAID" ? "#4caf50" : "#f44336",
                          borderColor: c.paymentStatus === "PAID" ? "#4caf50" : "#f44336",
                          fontWeight: "bold"
                        }}
                      />

                      <Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1, ml: 1 }}>
                        <IconButton 
                          onClick={() => { 
                            setNewCompany({ name: c.name, username: c.username, email: c.email, password: "" }); 
                            setEditIndex(companies.findIndex(comp => comp._id === c._id)); 
                            setOpenForm(true); 
                          }} 
                          sx={{ color: "#FFD600", "&:hover": { bgcolor: "#FFD600", color: "#000000" } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(c._id)} 
                          sx={{ color: "#ff4444", "&:hover": { bgcolor: "#ff0000", color: "#fff" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
              {paginatedCompanies.length === 0 && (
                <Box sx={{ textAlign: "center", py: 5, color: "#666666" }}>
                  NO REGISTRY FOUND
                </Box>
              )}
            </Stack>
          </Box>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredCompanies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: "#000000",
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectIcon": { color: "#FFD600" },
              "& .MuiIconButton-root": { color: "#FFD600" },
              "& .MuiTablePagination-select": { color: "#000000" },
              "& .MuiTypography-root": { color: "#000000" },
            }}
          />
        </Paper>

        {/* --- DIALOGS --- */}
        <Dialog open={openForm} onClose={() => setOpenForm(false)} TransitionComponent={Zoom} fullWidth maxWidth="xs" PaperProps={{ sx: whiteCard }}>
          <DialogTitle sx={{ color: "#000000", fontWeight: "bold" }}>
            {editIndex !== null ? "MODIFY ENTITY" : "NEW REGISTRY"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={1}>
              <TextField 
                label="Entity Name" 
                size="small" 
                fullWidth 
                value={newCompany.name} 
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} 
                sx={whiteInput} 
              />
              <TextField 
                label="Super Username" 
                size="small" 
                fullWidth 
                value={newCompany.username} 
                onChange={(e) => setNewCompany({ ...newCompany, username: e.target.value.toLowerCase() })} 
                sx={whiteInput}
                helperText="Username will be automatically lowercased"
              />
              <TextField 
                label="Registry Email" 
                size="small" 
                fullWidth 
                value={newCompany.email} 
                onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })} 
                sx={whiteInput} 
              />
              <TextField 
                label="Access Key" 
                size="small" 
                type="password" 
                fullWidth 
                value={newCompany.password} 
                onChange={(e) => setNewCompany({ ...newCompany, password: e.target.value })} 
                sx={whiteInput} 
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenForm(false)} sx={{ color: "#666666" }}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateOrUpdate} sx={yellowButton}>Confirm</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openPass} onClose={() => setOpenPass(false)} TransitionComponent={Zoom} fullWidth maxWidth="xs" PaperProps={{ sx: whiteCard }}>
          <DialogTitle sx={{ color: "#000000", fontWeight: "bold" }}>SECURITY OVERRIDE</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField 
                label="Current Key" 
                size="small" 
                type="password" 
                fullWidth 
                sx={whiteInput} 
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })} 
              />
              <TextField 
                label="New Key" 
                size="small" 
                type="password" 
                fullWidth 
                sx={whiteInput} 
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} 
              />
              <TextField 
                label="Confirm Key" 
                size="small" 
                type="password" 
                fullWidth 
                sx={whiteInput} 
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} 
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenPass(false)} sx={{ color: "#666666" }}>Dismiss</Button>
            <Button variant="contained" onClick={handleChangePassword} sx={yellowButton}>Update</Button>
          </DialogActions>
        </Dialog>

      </Container>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BenAdminDashboard;